import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import api from '../lib/api';
import { Button } from './ui/button';
import { LayoutGrid, Rows3 } from 'lucide-react';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from './ui/alert-dialog';
import { useTableNavigation } from '../hooks/useTableNavigation';
import { useDraft } from '../hooks/useDraft';

import { createEmptyRow, getDesignSizes, getSizeBands, getDesignColors, recomputeRowAmount, sortScannedRows, computeTotals, SIZE_ORDER } from './create-sales-order/helpers';
import SOHeader from './create-sales-order/SOHeader';
import CustomerDetailsCard from './create-sales-order/CustomerDetailsCard';
import SOScannerCard from './create-sales-order/SOScannerCard';
import InvoiceTable from './create-sales-order/InvoiceTable';
import MobileOrderList from './create-sales-order/MobileOrderList';
import SubstituteDialog from './create-sales-order/SubstituteDialog';
import DeleteRowDialog from './create-sales-order/DeleteRowDialog';
import StockWarningDialog from './create-sales-order/StockWarningDialog';

const CreateSalesOrder = ({ user }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const editSO = location.state?.editSO || null;
  const repeatSO = location.state?.repeatSO || null;

  // Master data
  const [customers, setCustomers] = useState([]);
  const [products, setProducts] = useState([]);
  const [agents, setAgents] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [brands, setBrands] = useState([]);
  const [events, setEvents] = useState([]);
  const [enums, setEnums] = useState({ design_themes: [] });

  // Form state
  const [selectedCustomer, setSelectedCustomer] = useState('');
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerDropdownOpen, setCustomerDropdownOpen] = useState(false);
  const [selectedAgent, setSelectedAgent] = useState('');
  const [selectedDistributor, setSelectedDistributor] = useState('');
  const [channel, setChannel] = useState('');
  const [transportName, setTransportName] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [dispatchDate, setDispatchDate] = useState(new Date().toISOString().split('T')[0]);
  const [orderDate, setOrderDate] = useState(new Date().toISOString().split('T')[0]);
  const [occasion, setOccasion] = useState('');
  const [eventId, setEventId] = useState('');
  const [newEventName, setNewEventName] = useState('');
  const [addingEvent, setAddingEvent] = useState(false);
  const [soNotes, setSoNotes] = useState('');
  const [customerWspType, setCustomerWspType] = useState('local');
  const [customerDiscount, setCustomerDiscount] = useState(0);

  // Table rows
  const [rows, setRows] = useState([createEmptyRow(1)]);
  const [saving, setSaving] = useState(false);

  // "Repeat last order" shortcut (loaded after a customer is picked)
  const [lastOrder, setLastOrder] = useState(null);
  const [confirmRepeatLast, setConfirmRepeatLast] = useState(false);

  // Design search + substitute dialog
  const [substituteDialog, setSubstituteDialog] = useState(null); // { rowIdx, product }
  const [substituteSearch, setSubstituteSearch] = useState('');
  const [bulkDiscount, setBulkDiscount] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null); // { idx, designCode }
  const [stockWarning, setStockWarning] = useState(null); // { warnings, payload }
  const [confirmCancel, setConfirmCancel] = useState(false);
  const [cardView, setCardView] = useState(typeof window !== 'undefined' && window.innerWidth < 1024);

  const customerRef = useRef(null);
  const handleTableKeyDown = useTableNavigation('so-invoice-table');
  const { hasDraft, setHasDraft, draftStatus, save: saveDraft, load: loadDraft, clear: clearDraft } = useDraft('SO', { enabled: !editSO, initialDraftId: location.state?.draftId || null });

  // Queue form data for auto-save every 5 seconds
  useEffect(() => {
    if (editSO || !dataLoaded) return;
    const hasContent = selectedCustomer || rows.some(r => r.product_id);
    if (!hasContent) return;
    const custName = customerSearch.split(' (')[0] || '';
    const itemCount = rows.filter(r => r.product_id).length;
    saveDraft(
      { rows, selectedCustomer, customerSearch, channel, occasion, eventId, orderDate, dispatchDate, soNotes, selectedAgent, selectedDistributor, transportName, paymentMode, customerWspType, customerDiscount },
      `${custName} — ${itemCount} item${itemCount !== 1 ? 's' : ''}`
    );
    // eslint-disable-next-line
  }, [rows, selectedCustomer, channel, occasion, eventId, orderDate, dispatchDate, soNotes, selectedAgent, selectedDistributor, transportName, paymentMode, customerWspType, customerDiscount]);

  const restoreDraft = async () => {
    const d = await loadDraft();
    if (!d) { setHasDraft(false); return; }
    if (d.rows) setRows(d.rows);
    setSelectedCustomer(d.selectedCustomer || '');
    setCustomerSearch(d.customerSearch || '');
    setChannel(d.channel || '');
    setOccasion(d.occasion || '');
    setEventId(d.eventId || '');
    if (d.dispatchDate) setDispatchDate(d.dispatchDate);
    if (d.orderDate) setOrderDate(d.orderDate);
    setSoNotes(d.soNotes || '');
    setSelectedAgent(d.selectedAgent || '');
    setSelectedDistributor(d.selectedDistributor || '');
    setTransportName(d.transportName || '');
    setPaymentMode(d.paymentMode || '');
    setCustomerWspType(d.customerWspType || 'local');
    setCustomerDiscount(d.customerDiscount || 0);
    setHasDraft(false);
    toast.success('Draft restored');
  };

  // Standard size list (for "forwarding" designs that have no PE/SKUs yet),
  // sorted in natural garment-size order.
  const standardSizes = useMemo(() => {
    const sizes = enums.sizes || [];
    return [...sizes].sort((a, b) => {
      const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
      return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
    });
  }, [enums.sizes]);

  // Scanner
  const [scannerActive, setScannerActive] = useState(false);
  const [facingMode, setFacingMode] = useState('environment');
  const [dataLoaded, setDataLoaded] = useState(false);
  const scannerRef = useRef(null);
  const scannerContainerId = 'so-qr-reader';
  const facingModeRef = useRef('environment');
  const productsRef = useRef([]);
  const rowsRef = useRef(rows);
  const customerWspTypeRef = useRef(customerWspType);
  const standardSizesRef = useRef(standardSizes);
  useEffect(() => { productsRef.current = products; }, [products]);
  useEffect(() => { rowsRef.current = rows; }, [rows]);
  useEffect(() => { customerWspTypeRef.current = customerWspType; }, [customerWspType]);
  useEffect(() => { standardSizesRef.current = standardSizes; }, [standardSizes]);

  // Close customer dropdown when clicking outside
  useEffect(() => {
    const handleClick = (e) => {
      if (customerRef.current && !customerRef.current.contains(e.target)) setCustomerDropdownOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Close design dropdowns when clicking outside any design cell
  useEffect(() => {
    const handleClick = (e) => {
      if (e.target.closest && e.target.closest('[data-so-design-cell]')) return;
      setRows(prev => prev.map(r => (r.showDesignDropdown ? { ...r, showDesignDropdown: false } : r)));
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  // Scanner handlers
  const handleScannedCode = useCallback((code) => {
    const prods = productsRef.current;
    let scanned = code.trim().toUpperCase();
    try { const qr = JSON.parse(code.trim()); if (qr.design) scanned = qr.design.toUpperCase(); } catch {}
    let product = prods.find(p => p.design_code?.toUpperCase() === scanned);
    let matchedSku = null;
    if (!product) {
      for (const p of prods) {
        const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === scanned);
        if (sku) { product = p; matchedSku = sku; break; }
      }
    }
    if (!product && (scanned.includes('_') || scanned.includes('-'))) {
      const parts = scanned.split(/[_-]/);
      if (parts.length >= 2) product = prods.find(p => p.design_code?.toUpperCase() === parts[0]);
    }
    if (!product) { toast.error(`Design "${scanned}" not found`); return; }
    const colors = getDesignColors(product);
    const color = matchedSku?.color || '';
    setRows(prev => {
      let sizes = [], bands = [], sizeRate = {}, sizeQty = {};
      if (color) {
        sizes = getDesignSizes(product, color, customerWspTypeRef.current, standardSizesRef.current);
        bands = getSizeBands(sizes);
        sizeRate = Object.fromEntries(sizes.map(s => [s.size, s.rate]));
        if (matchedSku?.size) sizeQty = { [matchedSku.size]: 1 };
      }
      const rowData = { product_id: product.id, design_code: product.design_code, designSearch: product.design_code, colors, product, showDesignDropdown: false, color, individualSizes: sizes, bands, sizeRate, sizeQty, qty: matchedSku?.size ? 1 : '', amount: 0 };
      rowData.amount = recomputeRowAmount({ ...rowData, discount: '' });
      const emptyIdx = prev.findIndex(r => !r.product_id);
      if (emptyIdx >= 0) { const next = [...prev]; next[emptyIdx] = { ...next[emptyIdx], ...rowData }; return next; }
      return [...prev, { ...createEmptyRow(prev.length + 1), ...rowData }];
    });
    toast.success(`Scanned: ${product.design_code}`);
  }, []);

  // Force-stop any live camera tracks (html5-qrcode sometimes leaves the stream
  // running on the device — this kills the camera light reliably).
  const hardStopTracks = () => {
    const el = document.getElementById(scannerContainerId);
    if (el) el.querySelectorAll('video').forEach(v => {
      const s = v.srcObject;
      if (s && s.getTracks) s.getTracks().forEach(t => t.stop());
      v.srcObject = null;
    });
  };

  const launchScanner = useCallback((mode) => {
    setTimeout(async () => {
      const el = document.getElementById(scannerContainerId);
      if (!el) { setScannerActive(false); return; }
      const { Html5Qrcode } = await import('html5-qrcode');
      const scanner = new Html5Qrcode(scannerContainerId);
      scannerRef.current = scanner;
      scanner.start({ facingMode: mode }, { fps: 10, qrbox: { width: 250, height: 150 }, aspectRatio: 2.0 }, (t) => handleScannedCode(t), () => {})
        .catch(() => { toast.error(`${mode === 'user' ? 'Front' : 'Back'} camera not available`); setScannerActive(false); });
    }, 250);
  }, [handleScannedCode]);

  const startScanner = useCallback(() => {
    setScannerActive(true);
    launchScanner(facingModeRef.current);
  }, [launchScanner]);

  const stopScanner = useCallback(async () => {
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { await scanner.stop(); } catch(e) {}
      try { scanner.clear(); } catch(e) {}
    }
    hardStopTracks();
    setScannerActive(false);
  }, []);

  const switchCamera = useCallback(async () => {
    const next = facingModeRef.current === 'environment' ? 'user' : 'environment';
    facingModeRef.current = next;
    setFacingMode(next);
    const scanner = scannerRef.current;
    scannerRef.current = null;
    if (scanner) {
      try { await scanner.stop(); } catch(e) {}
      try { scanner.clear(); } catch(e) {}
    }
    hardStopTracks();
    launchScanner(next);
  }, [launchScanner]);

  useEffect(() => { return () => { const sc = scannerRef.current; scannerRef.current = null; if (sc) { try { sc.stop().then(() => { try { sc.clear(); } catch(e) {} }).catch(() => {}); } catch(e) {} } hardStopTracks(); }; }, []);
  // Auto-focus first design search on load
  useEffect(() => {
    if (dataLoaded) {
      setTimeout(() => {
        const input = document.querySelector('[data-testid="so-design-search-0"]');
        if (input) input.focus();
      }, 300);
    }
  }, [dataLoaded]);

  useEffect(() => {
    const load = async () => {
      try {
        const [custRes, prodRes, agentRes, distRes, brandRes, enumRes, eventRes] = await Promise.all([
          api.get('/customers'), api.get('/products'), api.get('/agents'),
          api.get('/distributors'), api.get('/brands'), api.get('/enums'),
          api.get('/events'),
        ]);
        setCustomers(custRes.data.filter(c => c.is_active));
        setProducts(prodRes.data);
        setBrands(brandRes.data);
        setAgents(agentRes.data);
        setDistributors(distRes.data);
        setEnums(enumRes.data);
        setEvents(eventRes.data);

        // Compute std sizes from the fresh enums response (the standardSizes
        // memo isn't populated yet on this first pass).
        const stdSizes = [...(enumRes.data.sizes || [])].sort((a, b) => {
          const ia = SIZE_ORDER.indexOf(a), ib = SIZE_ORDER.indexOf(b);
          return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
        });
        if (editSO) {
          prefillEdit(custRes.data, prodRes.data, editSO, stdSizes);
        } else if (repeatSO) {
          // "Repeat order": copy ONLY the product rows (designs/colours/sizes/
          // qty/rates/discount) into a fresh draft — the customer is picked anew.
          setRows(buildRowsFromSO(prodRes.data, repeatSO, stdSizes, 'local'));
          clearDraft();
        }
        setDataLoaded(true);
      } catch { toast.error('Failed to load data'); }
    };
    load();
  }, []);

  // Build SO-form rows (one per design+colour, sizes aggregated) from a saved
  // SO. Used by edit pre-fill, "repeat order" and "repeat last order".
  // reprice=true → rates come from the current customer's catalogue tier
  // (so copying another customer's order bills at THIS customer's WSP).
  const buildRowsFromSO = (prods, so, stdSizes, wspType = 'local', reprice = false) => {
    const rows = [];
    let sr = 1;
    for (const item of (so.items || [])) {
      const prod = prods.find(p => p.id === item.product_id) || prods.find(p => p.design_code === item.design_code);
      const productId = item.product_id || prod?.id || '';
      const colors = getDesignColors(prod);
      const byColor = {};
      for (const v of (item.variants || [])) { (byColor[v.color] = byColor[v.color] || []).push(v); }
      for (const [color, vars] of Object.entries(byColor)) {
        const catalogSizes = getDesignSizes(prod, color, wspType, stdSizes);
        const indMap = Object.fromEntries(catalogSizes.map(s => [s.size, s.rate]));
        const sizeQty = {}, sizeRate = {};
        let disc = 0;
        for (const v of vars) {
          sizeQty[v.size] = (parseInt(sizeQty[v.size]) || 0) + (v.qty_ordered || 0);
          const tierRate = indMap[v.size];
          sizeRate[v.size] = reprice && tierRate != null && tierRate !== ''
            ? tierRate
            : (v.wsp != null ? v.wsp : (tierRate || 0));
          disc = v.discount_percent || disc;
        }
        const sizeSet = new Map(catalogSizes.map(s => [s.size, s.rate]));
        Object.keys(sizeQty).forEach(s => { if (!sizeSet.has(s)) sizeSet.set(s, sizeRate[s]); });
        const individualSizes = [...sizeSet.entries()].map(([size, rate]) => ({ size, rate }))
          .sort((a, b) => {
            const ia = SIZE_ORDER.indexOf(a.size), ib = SIZE_ORDER.indexOf(b.size);
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
          });
        const row = {
          sr, product_id: productId, design_code: item.design_code, designSearch: item.design_code,
          color, individualSizes, bands: getSizeBands(individualSizes), sizeQty, sizeRate,
          qty: '', rate: '', discount: disc || '', amount: 0, colors, product: prod, showDesignDropdown: false,
          openColorPicker: false, openSizePicker: false,
        };
        row.amount = recomputeRowAmount(row);
        rows.push(row); sr++;
      }
    }
    if (rows.length === 0) rows.push(createEmptyRow(1));
    return rows;
  };

  const prefillEdit = (custs, prods, so, stdSizes = standardSizes) => {
    const cust = custs.find(c => c.id === so.customer_id);
    if (cust) {
      setSelectedCustomer(cust.id);
      setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
      const ch = so.channel || cust.default_channel || 'direct';
      setChannel(ch);
      setSelectedAgent(so.agent_id || cust.agent_id || '');
      setSelectedDistributor(so.distributor_id || cust.distributor_id || '');
      setTransportName(so.transport_name || cust.transport_preference || '');
      setPaymentMode(so.payment_mode || cust.payment_mode || '');
      setCustomerWspType(cust.wsp_type || 'local');
      setCustomerDiscount(cust.discount_percent || 0);
    }
    setOccasion(so.occasion || 'office_visit');
    setEventId(so.event_id || '');
    setDispatchDate(so.dispatch_date || '');
    setOrderDate(so.order_date || so.so_date || new Date().toISOString().split('T')[0]);
    setSoNotes(so.notes || '');
    setRows(buildRowsFromSO(prods, so, stdSizes, cust?.wsp_type || 'local'));
  };

  const selectCustomer = (cust) => {
    setSelectedCustomer(cust.id);
    setCustomerSearch(cust.company_name + (cust.city ? ` (${cust.city})` : ''));
    setCustomerDropdownOpen(false);
    // Infer channel: explicit default_channel > agent_id > distributor_id > direct
    let ch = cust.default_channel || '';
    if (!ch) {
      if (cust.agent_id) ch = 'via_agent';
      else if (cust.distributor_id) ch = 'via_distributor';
      else ch = 'direct';
    }
    setChannel(ch);
    setSelectedAgent(ch === 'via_agent' ? (cust.agent_id || '') : '');
    setSelectedDistributor(ch === 'via_distributor' ? (cust.distributor_id || '') : '');
    setTransportName(cust.transport_preference || '');
    setPaymentMode(cust.payment_mode || '');
    setCustomerWspType(cust.wsp_type || 'local');
    const disc = cust.discount_percent || 0;
    setCustomerDiscount(disc);
    // Prefill discount + re-price every product row to THIS customer's WSP tier
    // (so an order copied from another customer bills at the new customer's rate).
    const newWsp = cust.wsp_type || 'local';
    setRows(prev => prev.map(r => {
      const nr = { ...r, discount: disc > 0 ? disc : '' };
      if (nr.product && nr.color) {
        const catalogSizes = getDesignSizes(nr.product, nr.color, newWsp, standardSizes);
        const rateMap = Object.fromEntries(catalogSizes.map(s => [s.size, s.rate]));
        const sizeRate = { ...nr.sizeRate };
        Object.keys(nr.sizeQty || {}).forEach(sz => {
          if (rateMap[sz] != null && rateMap[sz] !== '') sizeRate[sz] = rateMap[sz];
        });
        nr.sizeRate = sizeRate;
      }
      nr.amount = recomputeRowAmount(nr);
      return nr;
    }));
    if (disc > 0) setBulkDiscount(disc);
    // Surface this customer's most recent order for one-tap reorder
    setLastOrder(null);
    api.get(`/sales-orders?customer_id=${cust.id}`)
      .then(res => { const list = res.data || []; if (list.length) setLastOrder(list[0]); })
      .catch(() => {});
  };

  // Load the customer's most recent order's items into the current draft.
  // Keeps the selected customer + header; re-prices to this customer's tier.
  const doRepeatLast = () => {
    if (!lastOrder) return;
    setRows(buildRowsFromSO(products, lastOrder, standardSizes, customerWspType || 'local', true));
    setConfirmRepeatLast(false);
    toast.success(`Loaded items from ${lastOrder.so_number}`);
  };

  // Append the last order's items to whatever is already in the draft.
  const appendLast = () => {
    if (!lastOrder) return;
    const added = buildRowsFromSO(products, lastOrder, standardSizes, customerWspType || 'local', true)
      .filter(r => r.product_id && Object.keys(r.sizeQty || {}).length > 0);
    setRows(prev => {
      const base = prev.filter(r => r.product_id && Object.keys(r.sizeQty || {}).length > 0);
      const combined = [...base, ...added];
      const result = combined.length ? combined : [createEmptyRow(1)];
      return result.map((r, i) => ({ ...r, sr: i + 1 }));
    });
    setConfirmRepeatLast(false);
    toast.success(`Added items from ${lastOrder.so_number}`);
  };

  const handleRepeatLastClick = () => {
    if (rows.some(r => r.product_id && Object.keys(r.sizeQty || {}).length > 0)) setConfirmRepeatLast(true);
    else doRepeatLast();
  };

  useEffect(() => { if (!selectedCustomer) setLastOrder(null); }, [selectedCustomer]);

  const filteredCustomers = useMemo(() => {
    if (!customerSearch.trim()) return customers.slice(0, 20);
    const q = customerSearch.trim().toLowerCase();
    return customers.filter(c =>
      c.company_name?.toLowerCase().includes(q) ||
      c.city?.toLowerCase().includes(q) ||
      (/\d/.test(customerSearch) && c.phone?.includes(q))
    ).slice(0, 20);
  }, [customerSearch, customers]);

  const updateRow = (idx, field, value) => {
    setRows(prev => {
      const next = [...prev];
      const row = { ...next[idx] };
      row[field] = value;

      // Full-SKU typed detection (e.g., "1024_M_RED")
      if (field === 'designSearch' && value && value.length >= 3) {
        const searchUpper = value.trim().toUpperCase();
        let matchedProduct = null;
        let matchedSku = null;
        for (const p of products) {
          const sku = (p.skus || []).find(s => s.sku_code?.toUpperCase() === searchUpper);
          if (sku) { matchedProduct = p; matchedSku = sku; break; }
        }
        if (!matchedProduct && (searchUpper.includes('_') || searchUpper.includes('-'))) {
          const parts = searchUpper.split(/[_-]/);
          if (parts.length >= 2) {
            matchedProduct = products.find(p => p.design_code?.toUpperCase() === parts[0]);
            if (matchedProduct) matchedSku = matchedProduct.skus?.find(s => s.sku_code?.toUpperCase() === searchUpper) || null;
          }
        }
        if (matchedProduct && matchedSku) {
          const disc = customerDiscount || 0;
          const colors = getDesignColors(matchedProduct);
          const sizes = getDesignSizes(matchedProduct, matchedSku.color, customerWspType, standardSizes);
          const sizeRate = Object.fromEntries(sizes.map(s => [s.size, s.rate]));
          // Merge into an existing row for the same product+colour
          const existingIdx = next.findIndex((r, i) => i !== idx && r.product_id === matchedProduct.id && r.color === matchedSku.color);
          if (existingIdx >= 0) {
            const ex = { ...next[existingIdx], sizeQty: { ...next[existingIdx].sizeQty } };
            ex.sizeQty[matchedSku.size] = (parseInt(ex.sizeQty[matchedSku.size]) || 0) + 1;
            ex.amount = recomputeRowAmount(ex);
            next[existingIdx] = ex;
            row.designSearch = '';
            next[idx] = row;
            toast.success(`${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size} → +1`);
            const sorted = sortScannedRows(next);
            setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="so-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
            return sorted;
          }
          row.product_id = matchedProduct.id; row.design_code = matchedProduct.design_code; row.designSearch = matchedProduct.design_code;
          row.colors = colors; row.product = matchedProduct; row.showDesignDropdown = false;
          row.color = matchedSku.color;
          row.individualSizes = sizes; row.bands = getSizeBands(sizes); row.sizeRate = sizeRate;
          row.sizeQty = { [matchedSku.size]: 1 };
          row.qty = 1; row.discount = disc > 0 ? disc : '';
          row.amount = recomputeRowAmount(row);
          next[idx] = row;
          toast.success(`Scanned: ${matchedProduct.design_code} | ${matchedSku.color} | ${matchedSku.size}`);
          next.push(createEmptyRow(next.length + 1));
          const sorted = sortScannedRows(next);
          setTimeout(() => { const inputs = document.querySelectorAll('[data-testid^="so-design-search-"]'); const last = inputs[inputs.length - 1]; if (last) last.focus(); }, 100);
          return sorted;
        }
      }

      // Design search changed and no longer matches current design — reset row
      if (field === 'designSearch' && row.design_code && value !== row.design_code) {
        row.product_id = ''; row.design_code = ''; row.color = '';
        row.individualSizes = []; row.bands = []; row.sizeQty = {}; row.sizeRate = {};
        row.colors = []; row.product = null; row.rate = ''; row.qty = ''; row.amount = 0;
      }

      // Colour chosen — load that colour's sizes (reset size selection)
      if (field === 'color' && row.product) {
        const sizes = getDesignSizes(row.product, value, customerWspType, standardSizes);
        row.individualSizes = sizes;
        row.bands = getSizeBands(sizes);
        row.sizeRate = Object.fromEntries(sizes.map(s => [s.size, s.rate]));
        row.sizeQty = {};
        row.amount = 0;
      }

      // Master qty applies to every ticked size
      if (field === 'qty') {
        const sq = { ...row.sizeQty };
        Object.keys(sq).forEach(s => { sq[s] = value; });
        row.sizeQty = sq;
        row.amount = recomputeRowAmount(row);
      }

      // Uniform rate edit applies to every ticked size
      if (field === 'rate') {
        const sr = { ...row.sizeRate };
        Object.keys(row.sizeQty || {}).forEach(s => { sr[s] = value; });
        row.sizeRate = sr;
        row.amount = recomputeRowAmount(row);
      }

      if (field === 'discount') {
        row.amount = recomputeRowAmount(row);
      }

      next[idx] = row;
      return next;
    });
  };

  // --- Per-size selection handlers (used by SizePopover) ---
  const mutateRow = (idx, fn) => setRows(prev => {
    const next = [...prev];
    const row = { ...next[idx], sizeQty: { ...next[idx].sizeQty }, sizeRate: { ...next[idx].sizeRate } };
    fn(row);
    row.amount = recomputeRowAmount(row);
    next[idx] = row;
    return next;
  });

  const toggleSize = (idx, size) => mutateRow(idx, row => {
    if (size in row.sizeQty) { delete row.sizeQty[size]; }
    else { row.sizeQty[size] = parseInt(row.qty) || 1; }
  });

  const applyBand = (idx, band) => mutateRow(idx, row => {
    const masterQ = parseInt(row.qty) || 1;
    const allSel = band.sizes.every(s => s in row.sizeQty);
    if (allSel) band.sizes.forEach(s => { delete row.sizeQty[s]; });
    else band.sizes.forEach(s => { if (!(s in row.sizeQty)) row.sizeQty[s] = masterQ; });
  });

  const setSizeQtyHandler = (idx, size, val) => mutateRow(idx, row => { row.sizeQty[size] = val; });
  const setSizeRateHandler = (idx, size, val) => mutateRow(idx, row => { row.sizeRate[size] = val; });

  const selectAllSizes = (idx) => mutateRow(idx, row => {
    const masterQ = parseInt(row.qty) || 1;
    (row.individualSizes || []).forEach(s => { if (!(s.size in row.sizeQty)) row.sizeQty[s.size] = masterQ; });
  });

  const clearSizes = (idx) => mutateRow(idx, row => { row.sizeQty = {}; });

  const selectDesign = (idx, product) => {
    const colors = getDesignColors(product);
    setRows(prev => {
      const next = [...prev];
      const base = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, product, showDesignDropdown: false, rate: '', amount: 0,
      };
      if (colors.length === 1) {
        // Single colour — auto-select it and queue the size popover to open
        const color = colors[0];
        const sizes = getDesignSizes(product, color, customerWspType, standardSizes);
        next[idx] = {
          ...base, color, individualSizes: sizes, bands: getSizeBands(sizes),
          sizeRate: Object.fromEntries(sizes.map(s => [s.size, s.rate])), sizeQty: {},
          openColorPicker: false, openSizePicker: true,
        };
      } else {
        // Multiple (or zero) colours — open the colour picker to choose
        next[idx] = {
          ...base, color: '', individualSizes: [], bands: [], sizeQty: {}, sizeRate: {},
          openColorPicker: colors.length > 1, openSizePicker: false,
        };
      }
      return next;
    });
  };

  // Sizes (with tier rate) available for a given design + colour.
  const getSizesForColor = (product, color) => getDesignSizes(product, color, customerWspType, standardSizes);

  // Apply the combined multi-colour + per-colour-size selection: replace the
  // design row at idx with ONE row per selected colour (sizes/qty filled), then
  // auto-create a fresh row at the end and focus it.
  const applyMultiColor = (idx, selections) => {
    setRows(prev => {
      if (!selections || selections.length === 0) return prev;
      const base = prev[idx];
      const built = selections.map(sel => {
        const sizes = getSizesForColor(base.product, sel.color);
        const sizeSet = new Map(sizes.map(s => [s.size, s.rate]));
        const sizeRate = {};
        Object.keys(sel.sizeQty).forEach(sz => {
          sizeRate[sz] = sizeSet.has(sz) ? sizeSet.get(sz) : 0;
          if (!sizeSet.has(sz)) sizeSet.set(sz, sizeRate[sz]);
        });
        const individualSizes = [...sizeSet.entries()].map(([size, rate]) => ({ size, rate }))
          .sort((a, b) => {
            const ia = SIZE_ORDER.indexOf(a.size), ib = SIZE_ORDER.indexOf(b.size);
            return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
          });
        const row = {
          ...createEmptyRow(0),
          product_id: base.product_id, design_code: base.design_code, designSearch: base.design_code,
          color: sel.color, individualSizes, bands: getSizeBands(individualSizes),
          sizeQty: { ...sel.sizeQty }, sizeRate, discount: base.discount || '',
          colors: base.colors, product: base.product, showDesignDropdown: false,
        };
        row.amount = recomputeRowAmount(row);
        return row;
      });
      let next = [...prev.slice(0, idx), ...built, ...prev.slice(idx + 1)];
      const lastBuiltIdx = idx + built.length - 1;
      if (lastBuiltIdx === next.length - 1) {
        next = [...next, createEmptyRow(0)];
        const focusIdx = next.length - 1;
        setTimeout(() => document.querySelector(`[data-testid="so-design-search-${focusIdx}"]`)?.focus(), 130);
      }
      return next.map((r, i) => ({ ...r, sr: i + 1 }));
    });
  };

  const clearColorPicker = (idx) => setRows(prev => prev.map((r, i) => (i === idx && r.openColorPicker ? { ...r, openColorPicker: false } : r)));
  const clearSizePicker = (idx) => setRows(prev => prev.map((r, i) => (i === idx && r.openSizePicker ? { ...r, openSizePicker: false } : r)));

  // Auto-copy the previous row's selected sizes + qty when the size popover opens
  // on an empty row (fast repeat-entry of the same size run).
  const copyPrevSizes = (idx) => {
    if (idx <= 0) return;
    setRows(prev => {
      const cur = { ...prev[idx], sizeQty: { ...prev[idx].sizeQty }, sizeRate: { ...prev[idx].sizeRate } };
      if (Object.keys(cur.sizeQty).length > 0) return prev;
      const src = prev[idx - 1];
      const avail = new Set((cur.individualSizes || []).map(s => s.size));
      let copied = false;
      for (const [sz, q] of Object.entries(src.sizeQty || {})) {
        if (avail.has(sz)) { cur.sizeQty[sz] = q; copied = true; }
      }
      if (!copied) return prev;
      cur.qty = src.qty;
      cur.amount = recomputeRowAmount(cur);
      const next = [...prev];
      next[idx] = cur;
      return next;
    });
  };

  // After sizes are entered (popover closed) on the last row, auto-create the
  // next blank row and focus its design search — removes the manual ADD step.
  const onSizesDone = (idx) => {
    setRows(prev => {
      const row = prev[idx];
      const hasSizes = Object.keys(row.sizeQty || {}).length > 0;
      const isLast = idx === prev.length - 1;
      if (hasSizes && isLast) {
        const nextIdx = prev.length;
        setTimeout(() => { document.querySelector(`[data-testid="so-design-search-${nextIdx}"]`)?.focus(); }, 120);
        return [...prev, createEmptyRow(prev.length + 1)];
      }
      return prev;
    });
  };

  const confirmDesign = () => {
    setSubstituteDialog(null);
  };

  const selectSubstitute = (product) => {
    if (!substituteDialog) return;
    const idx = substituteDialog.rowIdx;
    const colors = getDesignColors(product);
    setRows(prev => {
      const next = [...prev];
      next[idx] = {
        ...next[idx], product_id: product.id, design_code: product.design_code,
        designSearch: product.design_code, colors, product,
        color: '', individualSizes: [], bands: [], sizeQty: {}, sizeRate: {}, rate: '', amount: 0,
      };
      return next;
    });
    setSubstituteDialog({ rowIdx: idx, product });
    setSubstituteSearch('');
  };

  const addRow = () => {
    const disc = parseFloat(bulkDiscount) || 0;
    setRows(prev => [...prev, { ...createEmptyRow(prev.length + 1), discount: disc > 0 ? disc : '' }]);
  };

  const removeRow = (idx) => {
    if (rows.length <= 1) return;
    setRows(prev => prev.filter((_, i) => i !== idx).map((r, i) => ({ ...r, sr: i + 1 })));
  };

  const copyDiscountToAll = (val) => {
    const disc = parseFloat(val) || 0;
    setRows(prev => prev.map(r => {
      const nr = { ...r, discount: disc > 0 ? disc : '' };
      nr.amount = recomputeRowAmount(nr);
      return nr;
    }));
    if (disc > 0) toast.success(`Discount ${disc}% applied to all rows`);
  };

  const filteredDesigns = (search) => {
    if (!search.trim()) return products.slice(0, 15);
    const q = search.trim().toLowerCase();
    return products.filter(p =>
      p.design_code.toLowerCase().includes(q) ||
      p.category?.toLowerCase().includes(q) ||
      p.fabric?.toLowerCase().includes(q)
    ).slice(0, 15);
  };

  const handleAddEvent = async () => {
    const name = newEventName.trim();
    if (!name) { toast.error('Enter event name'); return; }
    setAddingEvent(true);
    try {
      const res = await api.post('/events', { name });
      setEvents([...events, res.data].sort((a, b) => a.name.localeCompare(b.name)));
      setEventId(res.data.id);
      setNewEventName('');
      toast.success(`Event "${res.data.name}" created`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add event');
    } finally { setAddingEvent(false); }
  };

  const doSave = async (payload) => {
    setSaving(true);
    try {
      if (editSO) {
        await api.put(`/sales-orders/${editSO.id}`, payload);
        toast.success(`SO ${editSO.so_number} updated!`);
        clearDraft();
        navigate(`/sales-order/${editSO.id}`);
      } else {
        const res = await api.post('/sales-orders', payload);
        toast.success(`SO ${res.data.so_number} created!`);
        clearDraft();
        navigate('/sales-orders');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to save SO');
    } finally { setSaving(false); }
  };

  const submitSO = () => {
    if (!selectedCustomer) { toast.error('Select a customer'); return; }
    if (!occasion) { toast.error('Select Mode of Order'); return; }
    if (!channel) { toast.error('Select Channel'); return; }
    const validRows = rows.filter(r => r.product_id && r.color && Object.values(r.sizeQty || {}).some(q => parseInt(q) > 0));
    if (validRows.length === 0) { toast.error('Add at least one item with a size & qty'); return; }
    const grandAmt = validRows.reduce((s, r) => s + (r.amount || 0), 0);
    if (grandAmt === 0) { toast.error('Total amount cannot be zero'); return; }

    // Stock warnings (per ticked size)
    const warnings = [];
    for (const r of validRows) {
      if (!r.product) continue;
      for (const [size, q] of Object.entries(r.sizeQty)) {
        const qn = parseInt(q) || 0;
        if (qn <= 0) continue;
        const sku = r.product.skus?.find(s => s.color === r.color && s.size === size);
        if (!sku) continue; // forwarding design — stock not tracked
        const everStocked = (sku.opening_stock || 0) > 0 || (sku.current_stock || 0) > 0;
        if (!everStocked) continue; // SKU created (e.g. by a PO) but goods never received — treat as advance order
        const avail = Math.max(0, (sku.current_stock || 0) - (sku.reserved_stock || 0));
        if (qn > avail) warnings.push({ label: `${r.design_code} · ${r.color} ${size}`, ordered: qn, available: avail });
      }
    }

    // Group rows by product_id → SOItems with variants
    const itemsMap = {};
    for (const r of validRows) {
      const key = r.product_id;
      if (!itemsMap[key]) {
        itemsMap[key] = {
          product_id: r.product_id, design_code: r.design_code,
          brand_id: r.product?.brand_id || null, fabric: r.product?.fabric || null,
          images: r.product?.images || [], notes: null, variants: [],
        };
      }
      const disc = parseFloat(r.discount) || 0;
      for (const [size, q] of Object.entries(r.sizeQty)) {
        const qn = parseInt(q) || 0;
        if (qn <= 0) continue;
        itemsMap[key].variants.push({
          size, color: r.color, qty_ordered: qn,
          wsp: parseFloat((r.sizeRate || {})[size]) || 0,
          discount_percent: disc,
        });
      }
    }

    // Deduplicate variants: merge same size+color by summing qty
    for (const item of Object.values(itemsMap)) {
      const vMap = {};
      for (const v of item.variants) {
        const vKey = `${v.size}_${v.color}`;
        if (vMap[vKey]) vMap[vKey].qty_ordered += v.qty_ordered;
        else vMap[vKey] = { ...v };
      }
      item.variants = Object.values(vMap);
    }

    const payload = {
      customer_id: selectedCustomer,
      channel, occasion, event_id: eventId || null, payment_mode: paymentMode || null,
      agent_id: selectedAgent || null, distributor_id: selectedDistributor || null,
      transport_name: transportName || null, dispatch_date: dispatchDate || null, order_date: orderDate || null,
      items: Object.values(itemsMap), notes: soNotes,
    };

    if (warnings.length > 0) { setStockWarning({ warnings, payload }); return; }
    doSave(payload);
  };

  const getBrandName = (id) => brands.find(b => b.id === id)?.name || '';
  const custObj = customers.find(c => c.id === selectedCustomer);
  const { grandTotal, grandQty } = computeTotals(rows);
  return (
    <div className="p-6 space-y-4 animate-fadeIn" data-testid="create-so-page">
      <SOHeader editSO={editSO} onCancel={() => setConfirmCancel(true)} draftStatus={draftStatus} onManualSave={() => saveDraft()} />

      {repeatSO && !editSO && (
        <div className="flex items-center gap-2 rounded-lg border border-sky-300 bg-sky-50 px-4 py-2" data-testid="so-repeat-banner">
          <span className="text-sm text-sky-800">
            Repeating <strong>{repeatSO.so_number}</strong> — items copied. Pick any customer (rates re-price to their tier), review, and save as a new order.
          </span>
        </div>
      )}

      {hasDraft && !editSO && !repeatSO && (
        <div className="flex items-center justify-between rounded-lg border border-amber-300 bg-amber-50 px-4 py-2" data-testid="so-draft-banner">
          <span className="text-sm text-amber-800">You have an unsaved order from before — restore it?</span>
          <div className="flex gap-2">
            <Button size="sm" variant="outline" onClick={clearDraft} data-testid="so-draft-discard">Discard</Button>
            <Button size="sm" onClick={restoreDraft} className="bg-amber-600 hover:bg-amber-700" data-testid="so-draft-restore">Restore</Button>
          </div>
        </div>
      )}

      <CustomerDetailsCard
        customerRef={customerRef}
        customerSearch={customerSearch} setCustomerSearch={setCustomerSearch} setSelectedCustomer={setSelectedCustomer}
        customerDropdownOpen={customerDropdownOpen} setCustomerDropdownOpen={setCustomerDropdownOpen}
        filteredCustomers={filteredCustomers} selectCustomer={selectCustomer}
        occasion={occasion} setOccasion={setOccasion}
        eventId={eventId} setEventId={setEventId} events={events}
        newEventName={newEventName} setNewEventName={setNewEventName} handleAddEvent={handleAddEvent} addingEvent={addingEvent}
        orderDate={orderDate} setOrderDate={setOrderDate}
        dispatchDate={dispatchDate} setDispatchDate={setDispatchDate}
        channel={channel} setChannel={setChannel}
        selectedAgent={selectedAgent} setSelectedAgent={setSelectedAgent} agents={agents}
        selectedDistributor={selectedDistributor} setSelectedDistributor={setSelectedDistributor} distributors={distributors}
        transportName={transportName} setTransportName={setTransportName}
        soNotes={soNotes} setSoNotes={setSoNotes} custObj={custObj}
      />

      {lastOrder && !editSO && (
        <div className="flex items-center justify-between rounded-lg border border-violet-300 bg-violet-50 px-4 py-2" data-testid="so-repeat-last-banner">
          <span className="text-sm text-violet-800">
            This customer's last order: <strong>{lastOrder.so_number}</strong>
            {lastOrder.total_amount != null && <> · ₹{Number(lastOrder.total_amount).toLocaleString('en-IN')}</>}
          </span>
          <Button size="sm" onClick={handleRepeatLastClick} className="bg-violet-600 hover:bg-violet-700 h-8" data-testid="so-repeat-last-btn">
            ↻ Repeat last order
          </Button>
        </div>
      )}

      <SOScannerCard scannerActive={scannerActive} startScanner={startScanner} stopScanner={stopScanner} switchCamera={switchCamera} facingMode={facingMode} scannerContainerId={scannerContainerId} />

      <div className="flex justify-end">
        <Button variant="outline" size="sm" onClick={() => setCardView(v => !v)} className="gap-1.5 text-xs" data-testid="so-view-toggle">
          {cardView ? <><Rows3 className="w-3.5 h-3.5" /> Table view</> : <><LayoutGrid className="w-3.5 h-3.5" /> Card view</>}
        </Button>
      </div>

      {cardView ? (
        <MobileOrderList
          rows={rows} updateRow={updateRow} selectDesign={selectDesign} addRow={addRow} setDeleteConfirm={setDeleteConfirm}
          filteredDesigns={filteredDesigns} getBrandName={getBrandName} products={products}
          toggleSize={toggleSize} applyBand={applyBand} setSizeQty={setSizeQtyHandler} setSizeRate={setSizeRateHandler}
          selectAllSizes={selectAllSizes} clearSizes={clearSizes} grandQty={grandQty} grandTotal={grandTotal}
          getSizesForColor={getSizesForColor} applyMultiColor={applyMultiColor} clearColorPicker={clearColorPicker} clearSizePicker={clearSizePicker}
          setMasterQty={(i, v) => updateRow(i, 'qty', v)} onSizesDone={onSizesDone} copyPrevSizes={copyPrevSizes}
        />
      ) : (
        <InvoiceTable
          rows={rows} bulkDiscount={bulkDiscount} setBulkDiscount={setBulkDiscount} copyDiscountToAll={copyDiscountToAll}
          updateRow={updateRow} selectDesign={selectDesign} addRow={addRow} setDeleteConfirm={setDeleteConfirm}
          filteredDesigns={filteredDesigns} getBrandName={getBrandName} handleTableKeyDown={handleTableKeyDown} products={products}
          grandQty={grandQty} grandTotal={grandTotal}
          toggleSize={toggleSize} applyBand={applyBand} setSizeQty={setSizeQtyHandler} setSizeRate={setSizeRateHandler}
          selectAllSizes={selectAllSizes} clearSizes={clearSizes}
          getSizesForColor={getSizesForColor} applyMultiColor={applyMultiColor} clearColorPicker={clearColorPicker} clearSizePicker={clearSizePicker}
          setMasterQty={(i, v) => updateRow(i, 'qty', v)} onSizesDone={onSizesDone} copyPrevSizes={copyPrevSizes}
        />
      )}

      <SubstituteDialog
        substituteDialog={substituteDialog} confirmDesign={confirmDesign} getBrandName={getBrandName}
        substituteSearch={substituteSearch} setSubstituteSearch={setSubstituteSearch}
        filteredDesigns={filteredDesigns} selectSubstitute={selectSubstitute}
      />

      <DeleteRowDialog deleteConfirm={deleteConfirm} setDeleteConfirm={setDeleteConfirm} removeRow={removeRow} />

      <StockWarningDialog
        stockWarning={stockWarning} saving={saving}
        onCancel={() => setStockWarning(null)}
        onConfirm={() => { const p = stockWarning?.payload; setStockWarning(null); if (p) doSave(p); }}
      />

      {/* Sticky bottom action bar — Save stays here; Cancel moved to top-right */}
      <div className="sticky bottom-0 z-20 -mx-6 mt-2 flex items-center justify-end gap-2 border-t border-gray-200 bg-white/90 px-6 py-3 backdrop-blur">
        <Button onClick={submitSO} disabled={saving} className="bg-gradient-to-r from-sky-600 to-cyan-600" data-testid="so-save-btn">
          {saving ? 'Saving...' : editSO ? 'Update SO' : 'Create SO'}
        </Button>
      </div>

      <AlertDialog open={confirmCancel} onOpenChange={setConfirmCancel}>
        <AlertDialogContent data-testid="so-cancel-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this order?</AlertDialogTitle>
            <AlertDialogDescription>Any details you've entered will be lost. This cannot be undone.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="so-cancel-stay">Keep editing</AlertDialogCancel>
            <AlertDialogAction onClick={() => { clearDraft(); navigate('/sales-orders'); }} className="bg-red-600 hover:bg-red-700" data-testid="so-cancel-confirm">Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmRepeatLast} onOpenChange={setConfirmRepeatLast}>
        <AlertDialogContent data-testid="so-repeat-last-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Add {lastOrder?.so_number}'s items?</AlertDialogTitle>
            <AlertDialogDescription>
              You already have items in this order. Replace them with {lastOrder?.so_number}'s items, or append them to combine both?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="flex-col sm:flex-row gap-2">
            <AlertDialogCancel data-testid="so-repeat-last-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={appendLast} className="bg-emerald-600 hover:bg-emerald-700" data-testid="so-repeat-last-append">Append</AlertDialogAction>
            <AlertDialogAction onClick={doRepeatLast} className="bg-violet-600 hover:bg-violet-700" data-testid="so-repeat-last-confirm">Replace</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default CreateSalesOrder;
